import React, { useEffect } from 'react';
import {
    FieldsetControl,
    InputControl,
    SelectControl,
    LoaderMi,
    Row,
    Col,
    Textarea,
} from '@design-system-etat/dsfr-react';
import { useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getAPI } from 'store/apiReducer';
import InputFiles from 'components/Commun/Form/InputFiles';
import { getDateFormatWithSlash } from 'components/Commun/Form/maskFunctions';
import {
    formaterOptions,
    getMessageErreurChampManquant,
    getPatternHookFormValidation,
    validateDateNaissance,
    validateDepartement,
} from '../../../../utils/utils';
import {
    REGEXP_DATE_AVEC_MOIS_JOUR_INCONNU,
    REGEXP_LIEU,
    REGEXP_NOM_PRENOM,
} from '../../../../constants/contraintes.constants';
import SelectPays from '../../../../components/form/referentiel/select/SelectPays';
import SelectDepartements from '../../../../components/form/referentiel/select/SelectDepartement';
import {
    API_RECUPERER_INFORMATIONS_PERSONNELLES,
    CONTEXT_API_RECUPERER_INFORMATIONS_PERSONNELLES,
} from '../../../../constants/apiUrls.constants';
import usagerClientReducer from '../../../../store/usagerReducer';
import { choixSexe, typePieceIdentite } from '../../../../constants/select.constants';
import { formaterDataFormulaireModificationInforamtionsPersonnelles } from './FormatDataContact';
import BoutonTelechargementFichier from '../../../../components/BoutonTelechargementFichier/BoutonTelechargementFichier';
import { MESSAGE_DATE_NAISSANCE_INVALIDE } from '../../../../constants/messagePersonalise.constants';

const FormulaireContactPourModificationInformationsPersonnelles = ({
    getApi,
    dataCompteUsager,
    isLoadingCompteUsager,
}) => {
    const hookForm = useFormContext();
    const { register, formState, reset, getValues, watch } = hookForm;

    useEffect(() => {
        if (dataCompteUsager) {
            reset({
                theme: getValues('theme'),
                ...formaterDataFormulaireModificationInforamtionsPersonnelles(dataCompteUsager),
            });
        } else {
            getApi(
                usagerClientReducer,
                API_RECUPERER_INFORMATIONS_PERSONNELLES,
                CONTEXT_API_RECUPERER_INFORMATIONS_PERSONNELLES,
            );
        }
    }, []);

    useEffect(() => {
        if (isLoadingCompteUsager === false && dataCompteUsager) {
            const dataFormulaireFormates =
                formaterDataFormulaireModificationInforamtionsPersonnelles(dataCompteUsager);

            reset({
                theme: getValues('theme'),
                ...dataFormulaireFormates,
            });
        }
    }, [dataCompteUsager]);

    const validatePrenomUsuel = (valuePrenomUsuel) => {
        if (
            valuePrenomUsuel &&
            valuePrenomUsuel !== getValues('prenomUn') &&
            valuePrenomUsuel !== getValues('prenomDeux') &&
            valuePrenomUsuel !== getValues('prenomTrois') &&
            valuePrenomUsuel !== getValues('prenomQuatre')
        ) {
            return 'Le prénom usuel doit correspondre à un des autres prénoms';
        }
        return null;
    };

    const afficherPieceIdentite = () => {
        if (
            dataCompteUsager &&
            dataCompteUsager.personnePhysique &&
            dataCompteUsager.personnePhysique.pieceIdentite.piecesIdentiteFichier
        ) {
            return dataCompteUsager.personnePhysique.pieceIdentite.piecesIdentiteFichier.map(
                (pieceIdentiteFichier) => {
                    return (
                        <BoutonTelechargementFichier
                            libelle={pieceIdentiteFichier.filename}
                            uuidFichier={pieceIdentiteFichier.uuid}
                            key={pieceIdentiteFichier.uuid}
                        />
                    );
                },
            );
        }
        return null;
    };

    return (
        <>
            <FieldsetControl legend="Identité">
                <div className="position-relative margin-bottom-md">
                    {isLoadingCompteUsager && <LoaderMi taille="md" />}
                    <div>
                        <div>
                            <Row gutters>
                                <Col lg={6}>
                                    <SelectControl
                                        id="sexe"
                                        name="Sexe"
                                        label="Sexe *"
                                        options={formaterOptions(choixSexe)}
                                        required
                                        state={formState.errors?.sexe ? 'error' : null}
                                        message={formState.errors?.sexe?.message}
                                        {...register('sexe', {
                                            required: getMessageErreurChampManquant('sexe'),
                                        })}
                                        placeholder="-- Choisir une option --"
                                    />
                                </Col>
                                <Col lg={6}>
                                    <InputControl
                                        autocomplete="name"
                                        label="Nom *"
                                        uppercase
                                        {...register('nom', {
                                            required: getMessageErreurChampManquant('nom'),
                                            pattern: getPatternHookFormValidation(
                                                REGEXP_NOM_PRENOM,
                                                'nom',
                                            ),
                                        })}
                                        state={formState.errors?.nom ? 'error' : null}
                                        message={formState.errors?.nom?.message}
                                        required
                                    />
                                </Col>
                            </Row>
                            <Row gutters>
                                <Col lg={6}>
                                    <InputControl
                                        autocomplete="family-name"
                                        label="Nom d'usage"
                                        uppercase
                                        {...register('nomUsage')}
                                        disabled
                                    />
                                </Col>
                                <Col lg={6}>
                                    <div className="sia-input-alignementSansTexteAide">
                                        <InputControl
                                            autocomplete="given-name"
                                            label="Premier prénom *"
                                            uppercase
                                            {...register('prenomUn', {
                                                required:
                                                    getMessageErreurChampManquant('premier prénom'),
                                                pattern: getPatternHookFormValidation(
                                                    REGEXP_NOM_PRENOM,
                                                    'premier prénom',
                                                ),
                                            })}
                                            state={formState.errors?.prenomUn ? 'error' : null}
                                            message={formState.errors?.prenomUn?.message}
                                            required
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <Row gutters>
                                <Col lg={6}>
                                    <InputControl
                                        autocomplete="additional-name"
                                        label="Deuxième prénom"
                                        disabled={!watch('prenomUn')}
                                        {...register('prenomDeux', {
                                            pattern: getPatternHookFormValidation(
                                                REGEXP_NOM_PRENOM,
                                                'deuxième prénom',
                                            ),
                                        })}
                                        uppercase
                                        state={formState.errors?.prenomDeux ? 'error' : null}
                                        message={formState.errors?.prenomDeux?.message}
                                    />
                                </Col>
                                <Col lg={6}>
                                    <InputControl
                                        autocomplete="additional-name"
                                        label="Troisième prénom"
                                        disabled={!watch('prenomDeux')}
                                        {...register('prenomTrois', {
                                            pattern: getPatternHookFormValidation(
                                                REGEXP_NOM_PRENOM,
                                                'troisième prénom',
                                            ),
                                        })}
                                        uppercase
                                        state={formState.errors?.prenomTrois ? 'error' : null}
                                        message={formState.errors?.prenomTrois?.message}
                                    />
                                </Col>
                            </Row>
                            <Row gutters>
                                <Col lg={6}>
                                    <div className="sia-input-alignementSansTexteAide">
                                        <InputControl
                                            autocomplete="additional-name"
                                            label="Quatrième prénom"
                                            disabled={!watch('prenomTrois')}
                                            {...register('prenomQuatre', {
                                                pattern: getPatternHookFormValidation(
                                                    REGEXP_NOM_PRENOM,
                                                    'quatrième prénom',
                                                ),
                                            })}
                                            uppercase
                                            state={formState.errors?.prenomQuatre ? 'error' : null}
                                            message={formState.errors?.prenomQuatre?.message}
                                        />
                                    </div>
                                </Col>
                                <Col lg={6}>
                                    <InputControl
                                        autocomplete="given-name"
                                        label="Prénom usuel"
                                        {...register('prenomUsuel', {
                                            pattern: getPatternHookFormValidation(
                                                REGEXP_NOM_PRENOM,
                                                'prénom usuel',
                                            ),
                                            validate: (value) => validatePrenomUsuel(value),
                                        })}
                                        uppercase
                                        state={formState.errors?.prenomUsuel ? 'error' : null}
                                        message={formState.errors?.prenomUsuel?.message}
                                    />
                                </Col>
                            </Row>
                            <Row gutters>
                                <Col lg={6}>
                                    <InputControl
                                        autocomplete="bday"
                                        label="Date de naissance *"
                                        required
                                        placeholder="JJ/MM/AAAA"
                                        {...register('dateNaissance', {
                                            required: true,
                                            pattern: getPatternHookFormValidation(
                                                REGEXP_DATE_AVEC_MOIS_JOUR_INCONNU,
                                                '',
                                                MESSAGE_DATE_NAISSANCE_INVALIDE,
                                            ),
                                            validate: (value) => validateDateNaissance(value),
                                        })}
                                        state={formState.errors?.dateNaissance ? 'error' : null}
                                        message={formState.errors?.dateNaissance?.message}
                                        maskFonction={getDateFormatWithSlash}
                                    />
                                </Col>
                                <Col lg={6}>
                                    <SelectPays
                                        name="paysNaissance"
                                        label="Pays de naissance *"
                                        required
                                        register={register('paysNaissance', {
                                            required:
                                                getMessageErreurChampManquant('pays de Naissance'),
                                        })}
                                    />
                                </Col>
                            </Row>
                            <Row gutters>
                                <Col lg={6}>
                                    <SelectDepartements
                                        name="codePostalNaissance"
                                        label="Département de naissance *"
                                        placeholder="-- Choisir une option --"
                                        register={register('codePostalNaissance', {
                                            validate: (value) =>
                                                validateDepartement(
                                                    value,
                                                    getValues('paysNaissance'),
                                                ),
                                        })}
                                        disabled={watch('paysNaissance') !== 75}
                                    />
                                </Col>
                                <Col lg={6}>
                                    <InputControl
                                        autocomplete="street-address"
                                        label="Lieu de naissance *"
                                        uppercase
                                        required
                                        {...register('lieuNaissance', {
                                            required:
                                                getMessageErreurChampManquant('lieu de naissance'),
                                            pattern: getPatternHookFormValidation(
                                                REGEXP_LIEU,
                                                'lieu de naissance',
                                            ),
                                        })}
                                        state={formState.errors?.lieuNaissance ? 'error' : null}
                                        message={formState.errors?.lieuNaissance?.message}
                                    />
                                </Col>
                            </Row>
                            <Row gutters>
                                <Col lg={6}>
                                    <SelectPays
                                        name="nationalite"
                                        label="Nationalité *"
                                        required
                                        placeholder="-- Choisir une option --"
                                        register={register('nationalite', {
                                            required: 'Ce champ est obligatoire',
                                        })}
                                        disabled
                                    />
                                </Col>
                            </Row>
                            <Row gutters horizontalAlign="center" verticalAlign="bottom">
                                <Col lg={6}>
                                    <div className="sia-input-alignementSansTexteAide">
                                        <SelectControl
                                            id="typePieceIdentite"
                                            name="typePieceIdentite"
                                            label="Sélectionnez votre type de pièce d'identité *"
                                            options={formaterOptions(typePieceIdentite)}
                                            disabled
                                            required
                                            {...register('typePieceIdentite', {
                                                required: 'Ce champ est obligatoire',
                                            })}
                                            placeholder="-- Choisir une option --"
                                        />
                                    </div>
                                </Col>
                                <Col lg={6}>
                                    <InputControl
                                        label="Numéro de la pièce d'identité *"
                                        {...register('numeroPieceIdentite', {
                                            required: 'Ce champ est obligatoire',
                                        })}
                                        disabled
                                        required
                                    />
                                </Col>
                                {afficherPieceIdentite()}
                            </Row>
                        </div>
                    </div>
                </div>
                <div>
                    <label className="fr-label" htmlFor="message">
                        Votre message *
                    </label>
                    <p className="fr-hint-text">
                        Veuillez saisir votre message (4000 caractères maximum){' '}
                    </p>
                    <Textarea
                        id="message"
                        name="message"
                        maxlength="4000"
                        {...register('message', {
                            required: 'Ce champ est obligatoire',
                        })}
                        required
                    />
                    <div className="margin-top-md margin-bottom-md">
                        <InputFiles
                            label="Si nécessaire vous pouvez joindre un ou plusieurs fichiers"
                            name="piecesJointes"
                            multiple
                            error={formState.errors ? formState.errors.piecesJointes : null}
                        />
                    </div>
                </div>
            </FieldsetControl>
        </>
    );
};

FormulaireContactPourModificationInformationsPersonnelles.propTypes = {
    getApi: PropTypes.func.isRequired,
    dataCompteUsager: PropTypes.shape({
        personnePhysique: PropTypes.shape({
            nom: PropTypes.string.isRequired,
            nomUsage: PropTypes.string.isRequired,
            prenomUn: PropTypes.string.isRequired,
            prenomDeux: PropTypes.string,
            prenomTrois: PropTypes.string,
            prenomQuatre: PropTypes.string,
            prenomUsuel: PropTypes.string.isRequired,
            sexe: PropTypes.string,
            dateNaissance: PropTypes.string,
            lieuNaissance: PropTypes.string,
            codePostalNaissance: PropTypes.string,
            paysNaissance: PropTypes.shape({
                id: PropTypes.string.isRequired,
                libelle: PropTypes.string.isRequired,
            }).isRequired,
            nationalite: PropTypes.shape({
                id: PropTypes.string.isRequired,
                libelle: PropTypes.string.isRequired,
            }).isRequired,
            pieceIdentite: PropTypes.shape({
                typePieceIdentite: PropTypes.string,
                numeroPieceIdentite: PropTypes.string,
                piecesIdentiteFichier: PropTypes.arrayOf(
                    PropTypes.shape({
                        uuid: PropTypes.string.isRequired,
                        filename: PropTypes.string.isRequired,
                    }),
                ),
            }),
        }).isRequired,
    }),
    isLoadingCompteUsager: PropTypes.bool,
};

export default connect(
    (state) => {
        const { isLoading: isLoadingCompteUsager, data: dataCompteUsager } =
            state.monCompte.recupererInformationsPersonnelles;
        return {
            dataCompteUsager,
            isLoadingCompteUsager,
        };
    },
    {
        getApi: getAPI,
    },
)(FormulaireContactPourModificationInformationsPersonnelles);
